import { t } from '@lingui/core/macro';
import type { ReactNode } from 'react';

import type { OptionType } from '@/api/common';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type SelectProps,
} from '@/components/Form/pickers/primitives/select';
import SelectEmpty from '@/components/Form/pickers/primitives/select/SelectEmpty';
import SelectStatus from '@/components/Form/pickers/primitives/select/SelectStatus';

export type SelectPickerProps<T extends OptionType> = Omit<
  SelectProps<T, false>,
  'value' | 'onValueChange' | 'itemToStringValue' | 'itemToStringLabel'
> & {
  options?: T[];
  value?: T | null;
  onChange?: (value: T | null) => void;
  className?: string;
  isLoading?: boolean;
  error?: Error | null;
  placeholder?: string;
  notFoundContent?: ReactNode;
  renderOption?: (option: T) => ReactNode;
};

const SelectPicker = <T extends OptionType>({
  options,
  value,
  onChange,
  className,
  isLoading = false,
  error,
  placeholder = t`Select...`,
  notFoundContent,
  renderOption,
  ...rest
}: SelectPickerProps<T>) => {
  return (
    <Select
      itemToStringValue={(item) => item.id}
      itemToStringLabel={(item) => item.name ?? ''}
      value={value ?? null}
      onValueChange={(selectedValue) => onChange?.(selectedValue ?? null)}
      isLoading={isLoading}
      error={error}
      {...rest}
    >
      <SelectTrigger className={className}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectStatus />
          <SelectEmpty items={options}>{notFoundContent}</SelectEmpty>
          {options?.map((option) => (
            <SelectItem key={option.id} value={option} disabled={option.disabled}>
              {renderOption ? renderOption(option) : option.name}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};

export default SelectPicker;
